import { useMutation, useQueryClient } from 'react-query'
import { bulkUserManagement, downloadUserManagementTemplate } from 'Services/users/userService'
import type {
  BulkUserManagementPayload,
  BulkUserManagementResponse
} from 'Services/apiResponseTypes'
import { toast } from 'react-toastify'
import { AppNotification } from 'Utils/types/appNotification'
import { QueryNames } from 'Hooks/api/queryNames'
import { useErrorReporter } from 'Hooks/useErrorReporter'

export const useBulkUserManagementQuery = () => {
  const reportError = useErrorReporter()
  const queryClient = useQueryClient()

  return useMutation<BulkUserManagementResponse, Error, BulkUserManagementPayload>(
    async (payload) => {
      const response = await bulkUserManagement(payload)
      return response
    },
    {
      onSuccess: (data, variables) => {
        if (!variables.dryRun) {
          queryClient.invalidateQueries([QueryNames.UsersForEmployer])
          toast.success(AppNotification.BulkUserManagementSuccess)
        }
      },
      onError: (error) => {
        reportError(`useBulkUserManagementQuery error: ${error.message}`)
        toast.error(AppNotification.BulkUserManagementFailure)
      }
    }
  )
}

export const useDownloadUserManagementTemplate = () => {
  const reportError = useErrorReporter()

  return useMutation<void, Error, { employerId: number; employerName?: string }>(
    async ({ employerId, employerName }) => {
      const blob = await downloadUserManagementTemplate(employerId)

      let url: string | null = null
      let link: HTMLAnchorElement | null = null

      try {
        url = window.URL.createObjectURL(blob)
        link = document.createElement('a')
        link.href = url

        const timestamp = new Date().toISOString().split('T')[0]
        const sanitizedName = employerName?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'employer'
        link.download = `${sanitizedName}_users_${timestamp}.csv`

        document.body.appendChild(link)
        link.click()
      } finally {
        if (link && document.body.contains(link)) {
          document.body.removeChild(link)
        }
        if (url) {
          window.URL.revokeObjectURL(url)
        }
      }
    },
    {
      onError: (error) => {
        reportError(`useDownloadUserManagementTemplate error: ${error.message}`)
        toast.error(AppNotification.DownloadTemplateFailure)
      }
    }
  )
}
